"use client"

import { createContext, useContext, useEffect, useState, ReactNode } from "react"

interface DarkModeContextType {
  isDark: boolean
  toggleDarkMode: () => void
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined)

// Helper functions for cookie management
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

function setCookie(name: string, value: string, days: number = 365) {
  if (typeof document === 'undefined') return
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

export function DarkModeProvider({ children }: { children: ReactNode }) {
  // Default should be dark; user can explicitly switch to light.
  const [isDark, setIsDark] = useState(true)
  const [mounted, setMounted] = useState(false)

  // Initialize from cookies on mount (with localStorage fallback)
  useEffect(() => {
    setMounted(true)

    // Try cookie first
    let savedMode = getCookie("darkMode")

    // Fallback to localStorage if cookie doesn't exist (for migration)
    if (!savedMode && typeof localStorage !== 'undefined') {
      try {
        savedMode = localStorage.getItem("darkMode")
        // Migrate to cookie
        if (savedMode) {
          setCookie("darkMode", savedMode)
        }
      } catch (e) {
        console.warn("localStorage not available:", e)
      }
    }

    // Default to DARK mode if nothing saved
    if (savedMode === null) {
      setIsDark(true)
      document.documentElement.classList.add("dark")
      return
    }

    // If explicitly saved: "true" = dark, "false" = light
    if (savedMode === "true") {
      setIsDark(true)
      document.documentElement.classList.add("dark")
    } else {
      setIsDark(false)
      document.documentElement.classList.remove("dark")
    }
  }, [])

  const toggleDarkMode = () => {
    setIsDark((prev) => {
      const newValue = !prev

      // Save to cookie (primary storage)
      setCookie("darkMode", String(newValue))

      // Also save to localStorage as backup (if available)
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem("darkMode", String(newValue))
        }
      } catch (e) {
        console.warn("localStorage not available:", e)
      }

      // Toggle dark class on html element
      if (newValue) {
        document.documentElement.classList.add("dark")
      } else {
        document.documentElement.classList.remove("dark")
      }

      return newValue
    })
  }

  // Prevent flash of unstyled content
  if (!mounted) {
    return <>{children}</>
  }

  return (
    <DarkModeContext.Provider value={{ isDark, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  )
}

export function useDarkMode() {
  const context = useContext(DarkModeContext)

  // Jeśli context nie istnieje (np. podczas SSR), zwróć domyślne wartości
  if (context === undefined) {
    // W trybie produkcyjnym nie rzucaj błędu, tylko zwróć defaults
    if (typeof window === 'undefined') {
      return { isDark: false, toggleDarkMode: () => { } }
    }
    throw new Error("useDarkMode must be used within a DarkModeProvider")
  }

  return context
}
